import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { clientPath } from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";
import { Plus, Search, Copy, ArrowRight, Leaf } from "lucide-react";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function AddClientModal({ open, onClose, onCreated }) {
  const [data, setData] = useState({
    name: "", phone: "", email: "", goal: "", notes: "",
    consultation_notes: "", paid_until: "", package_name: "",
    follow_up_day: "", next_follow_up_date: "",
  });
  const [loading, setLoading] = useState(false);

  if (!open) return null;
  const set = (k, v) => setData({ ...data, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...data };
      // Coerce empty date strings to null so backend doesn't reject
      payload.paid_until = data.paid_until || null;
      payload.next_follow_up_date = data.next_follow_up_date || null;
      const { data: c } = await api.post("/admin/clients", payload);
      toast.success(`${c.name} added — link ready to share`);
      onCreated(c);
      onClose();
      setData({ name: "", phone: "", email: "", goal: "", notes: "", consultation_notes: "", paid_until: "", package_name: "", follow_up_day: "", next_follow_up_date: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add client");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--brand-ink))]/40 p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl border border-[hsl(var(--brand-border))] w-full max-w-xl p-8 shadow-xl max-h-[92vh] overflow-y-auto"
        data-testid="add-client-form"
      >
        <div className="font-display text-3xl mb-1">New client</div>
        <div className="text-sm text-[hsl(var(--brand-muted))] mb-6">A unique private link will be generated.</div>

        <div className="grid md:grid-cols-2 gap-4">
          {[
            { k: "name", label: "Full name", required: true, placeholder: "Pratham Sharma" },
            { k: "phone", label: "Phone (WhatsApp)", placeholder: "+91 98xxxxxxxx" },
            { k: "email", label: "Email (optional)", placeholder: "pratham@example.com", type: "email" },
            { k: "goal", label: "Goal", placeholder: "Weight loss, PCOS, muscle gain…" },
            { k: "package_name", label: "Package", placeholder: "1 month, 3 months…" },
            { k: "paid_until", label: "Paid until", type: "date" },
            { k: "follow_up_day", label: "Follow-up day", select: WEEKDAYS },
            { k: "next_follow_up_date", label: "Next follow-up", type: "date" },
          ].map((f) => (
            <div key={f.k}>
              <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1.5">{f.label}</label>
              {f.select ? (
                <select value={data[f.k]} onChange={(e) => set(f.k, e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md bg-white" data-testid={`client-field-${f.k}`}>
                  <option value="">—</option>
                  {f.select.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  required={f.required}
                  type={f.type || "text"}
                  placeholder={f.placeholder}
                  value={data[f.k]}
                  onChange={(e) => set(f.k, e.target.value)}
                  className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md focus:ring-2 focus:ring-[hsl(var(--brand-sage))]/30 focus:border-[hsl(var(--brand-sage))] focus:outline-none"
                  data-testid={`client-field-${f.k}`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="mt-4">
          <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1.5">Quick notes</label>
          <textarea
            rows={2}
            value={data.notes}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Short note — e.g. prefers vegetarian"
            className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md"
            data-testid="client-field-notes"
          />
        </div>
        <div className="mt-4">
          <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1.5">Opening consultation notes</label>
          <textarea
            rows={6}
            value={data.consultation_notes}
            onChange={(e) => set("consultation_notes", e.target.value)}
            placeholder="Everything from your 1-hour consultation — lifestyle, preferences, health markers, restrictions, goals…"
            className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md"
            data-testid="client-field-consultation-notes"
          />
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm hover:bg-[hsl(var(--brand-cream-deep))]" data-testid="add-client-cancel">
            Cancel
          </button>
          <button disabled={loading} type="submit" className="px-5 py-2.5 rounded-full bg-[hsl(var(--brand-sage))] text-white text-sm hover:bg-[hsl(var(--brand-sage-hover))] disabled:opacity-60" data-testid="add-client-submit">
            {loading ? "Saving…" : "Create client"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const load = async (q = "") => {
    setLoading(true);
    const { data } = await api.get(`/admin/clients${q ? `?search=${encodeURIComponent(q)}` : ""}`);
    setClients(data);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const copyLink = async (token, name) => {
    const url = `${window.location.origin}${clientPath(name, token)}`;
    const ok = await copyToClipboard(url);
    if (ok) toast.success(`Copied ${name}'s link`);
    else toast.error("Copy blocked — long-press the link to copy manually");
  };

  return (
    <div data-testid="admin-clients-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))]">Clients</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">Your roster</h1>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))]"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4" /> Add client
        </button>
      </div>

      <div className="mt-8 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--brand-muted))]" />
        <input
          placeholder="Search clients…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            load(e.target.value);
          }}
          className="w-full pl-9 pr-4 py-2.5 border border-[hsl(var(--brand-border))] rounded-md bg-white"
          data-testid="clients-search-input"
        />
      </div>

      <div className="mt-6 bg-white rounded-xl border border-[hsl(var(--brand-border))] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[hsl(var(--brand-muted))] font-serif-italic">Loading…</div>
        ) : clients.length === 0 ? (
          <div className="p-16 text-center">
            <Leaf className="w-10 h-10 mx-auto text-[hsl(var(--brand-sage))]" />
            <div className="font-display text-2xl mt-4">No clients yet</div>
            <div className="text-sm text-[hsl(var(--brand-muted))] mt-1">Add your first to generate their private link.</div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[hsl(var(--brand-cream-deep))]">
              <tr className="text-left text-xs tracking-[0.2em] uppercase text-[hsl(var(--brand-muted))]">
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3 hidden md:table-cell">Phone</th>
                <th className="px-5 py-3 hidden lg:table-cell">Paid until</th>
                <th className="px-5 py-3">Plan</th>
                <th className="px-5 py-3">Link</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-t border-[hsl(var(--brand-border))] hover:bg-[hsl(var(--brand-cream))]" data-testid={`client-row-${c.id}`}>
                  <td className="px-5 py-4">
                    <Link to={`/admin/clients/${c.id}`} className="font-medium text-[hsl(var(--brand-ink))] hover:text-[hsl(var(--brand-sage))]">
                      {c.name}
                    </Link>
                    {c.goal ? <div className="text-xs text-[hsl(var(--brand-muted))]">{c.goal}</div> : null}
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell text-sm text-[hsl(var(--brand-muted))]">{c.phone || "—"}</td>
                  <td className="px-5 py-4 hidden lg:table-cell text-sm text-[hsl(var(--brand-muted))]">{c.paid_until || "—"}</td>
                  <td className="px-5 py-4">
                    {c.active_plan ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[hsl(var(--brand-sage))]/10 text-[hsl(var(--brand-sage))]">
                        Active · ends {c.active_plan.end_date}
                      </span>
                    ) : (
                      <span className="text-xs text-[hsl(var(--brand-muted))]">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <button
                      onClick={() => copyLink(c.magic_token, c.name)}
                      className="inline-flex items-center gap-1 text-xs text-[hsl(var(--brand-sage))] hover:text-[hsl(var(--brand-sage-hover))]"
                      data-testid={`copy-link-${c.id}`}
                    >
                      <Copy className="w-3 h-3" /> Copy link
                    </button>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Link to={`/admin/clients/${c.id}`} className="text-sm text-[hsl(var(--brand-ink))] inline-flex items-center gap-1 hover:gap-2 transition-all">
                      Open <ArrowRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AddClientModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(c) => setClients((prev) => [c, ...prev])}
      />
    </div>
  );
}
